import * as React from 'react';
import { BuyerCustomerDTO, Permission, Res } from 'core/api';
import { FormComponent, Button, Modal, ErrorBlock, Col, Cols } from 'core/ui';
import { Binding, Result, SessionProvider } from 'core/infrastructure';
import { Command } from 'pos/common';
import classnames from 'classnames';

type Props<T> = {
	linkTo: Binding.PropertyPath<T>;
	boundValue?: BuyerCustomerDTO;
	onCustomerChanged: (customer: BuyerCustomerDTO) => void;
}

type State = { customer: Nullable<BuyerCustomerDTO>, errors: string[] }

export class CustomerComponent<T> extends React.Component<Props<T>, State> implements FormComponent {

	constructor(props: Props<T>) {
		super(props);
		this.state = {
			customer: this.props.boundValue || null,
			errors: []
		};
	}

	getPath(): string {
		return Binding.propertyPathToString(this.props.linkTo);
	}

	showErrors(errors: string[]): void {
		this.setState({ errors });
	}

	getValue(): Result<BuyerCustomerDTO> {
		const emptyCustomer: BuyerCustomerDTO = {
			Address: {
				City: '',
				Region: '',
				Street: '',
				ZipCode: ''
			},
			ApplySurcharge: false,
			BusinessName: '',
			Cif: '',
			DiscountRate: 0,
			FiscalName: '',
			CountryCode: '',
			Id: 0
		};
		return Result.ok(this.state.customer || emptyCustomer);
	}

	render() {

		const onChangeCustomer = async () => {
			if (!SessionProvider.hasPermission(Permission.Ticket_SetBuyerCustomer)) {
				await Modal.showMessage(Res.Msg.MSG_PERMISSION_DENIED_FOR(Permission.toString(Permission.Ticket_SetBuyerCustomer)));
				return;
			}

			const result = await Command.execute('searchCustomerByText');
			if (result.ok)
				this.setState({ customer: result.value }, () => this.props.onCustomerChanged(result.value));
		};

		const hasCustomer = this.state.customer && this.state.customer.Id !== 0;
		const discountPercentage = this.state.customer?.DiscountRate ?? 0 * 100;
		const fiscalName = hasCustomer ? this.state.customer?.FiscalName : Res.Web.CONFIRM_DOCUMENT_DATA_THERE_IS_NOT_CUSTOMER_ASSIGNED_LBL();

		return <Cols>
			<Col width='2ex' className='text-muted text-26 m-l-md'>
				<i className='fa fa-id-card-o' />
			</Col>
			<Col width='*' className='p-l'>
				<p className='text-26 text-muted'>{fiscalName}</p>
				<p className='text-14 p-b-sm'>
					{this.state.customer?.Cif}
					<br />
					{discountPercentage > 0 ? Res.Web.DISCOUNT_CONTROL_IN_PERCENTAGE_LBL(discountPercentage) : null}
				</p>

				<p className={classnames({ 'has-error': this.state.errors.length > 0 })}>
					<ErrorBlock msg={this.state.errors.join('\n')} />
				</p>

				<Button buttonStyle='info' outline onClick={onChangeCustomer}>
					{hasCustomer ? Res.Web.CONFIRM_DOCUMENT_DATA_CHANGE_CUSTOMER_BTN() : Res.Web.CONFIRM_DOCUMENT_DATA_SET_CUSTOMER_BTN()}
				</Button>
			</Col>
		</Cols>;
	}
}

export type CustomerComponentCtor<T> = new (props: Props<T>) => React.Component<Props<T>, State>;
